/**
 * useSpeechToText Hook - Web Speech API Integration
 * 
 * Provides push-to-talk speech recognition using the Web Speech API.
 * - Push-to-talk only (no always listening)
 * - Browser support detection with graceful fallback
 * - Permission error handling
 * - Duplicate prevention
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// Debug logging flag - set to false for production
const DEBUG_STT = false;

const log = (...args) => {
  if (DEBUG_STT) {
    console.log('[STT]', ...args);
  }
};

/**
 * Check if Web Speech API is supported
 */
export const isSpeechRecognitionSupported = () => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
};

/**
 * Get SpeechRecognition constructor
 */
const getSpeechRecognition = () => {
  return window.SpeechRecognition || window.webkitSpeechRecognition;
};

/**
 * Speech-to-Text states
 */
export const STT_STATES = {
  IDLE: 'idle',
  LISTENING: 'listening',
  PROCESSING: 'processing',
  ERROR: 'error',
  UNSUPPORTED: 'unsupported',
  PERMISSION_DENIED: 'permission_denied'
};

/**
 * Custom hook for push-to-talk speech recognition
 * 
 * @param {Object} options
 * @param {string} options.language - Recognition language (default: 'en-US')
 * @param {boolean} options.interimResults - Show interim results (default: true)
 * @param {Function} options.onResult - Callback when final transcript is ready
 * @param {Function} options.onInterim - Callback for interim results (optional)
 * @param {Function} options.onError - Callback for errors
 */
export const useSpeechToText = ({
  language = 'en-US',
  interimResults = true,
  onResult,
  onInterim,
  onError
} = {}) => {
  const [state, setState] = useState(
    isSpeechRecognitionSupported() ? STT_STATES.IDLE : STT_STATES.UNSUPPORTED
  );
  const [interimTranscript, setInterimTranscript] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  // Accumulate all final transcripts while button is held
  const accumulatedRef = useRef('');
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  
  /**
   * Initialize SpeechRecognition instance
   */
  const initRecognition = useCallback(() => {
    if (!isSpeechRecognitionSupported()) {
      log('Web Speech API not supported');
      return null;
    }
    
    const SpeechRecognition = getSpeechRecognition();
    const recognition = new SpeechRecognition();
    
    recognition.lang = language;
    // MUST be continuous for push-to-talk — otherwise pauses auto-submit
    recognition.continuous = true;
    recognition.interimResults = interimResults;
    recognition.maxAlternatives = 1;
    
    // Handle results — accumulate until button released
    recognition.onresult = (event) => {
      log('Recognition result event:', event);
      
      let interim = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        
        if (result.isFinal) {
          accumulatedRef.current += transcript;
        } else {
          interim += transcript;
        }
      }
      
      // Show full accumulated + interim for UI feedback
      const display = accumulatedRef.current + (interim ? interim : '');
      if (display) {
        setInterimTranscript(display);
        onInterim?.(display);
      }
    };
    
    recognition.onspeechstart = () => { log('Speech detected'); };
    recognition.onspeechend = () => { log('Speech ended'); };
    
    // Handle recognition end — deliver accumulated transcript
    recognition.onend = () => {
      log('Recognition ended, accumulated:', accumulatedRef.current);
      isListeningRef.current = false;
      
      const finalText = accumulatedRef.current.trim();
      accumulatedRef.current = '';
      
      if (finalText) {
        setState(STT_STATES.PROCESSING);
        onResultRef.current?.(finalText);
        setTimeout(() => {
          setState(prev => prev === STT_STATES.PROCESSING ? STT_STATES.IDLE : prev);
          setInterimTranscript('');
        }, 300);
      } else {
        setState(STT_STATES.IDLE);
        setInterimTranscript('');
      }
    };
    
    // Handle errors
    recognition.onerror = (event) => {
      log('Recognition error:', event.error);
      isListeningRef.current = false;
      
      let errorMsg = '';
      
      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          errorMsg = 'Mic permission denied. Please enable it in browser settings.';
          setState(STT_STATES.PERMISSION_DENIED);
          break;
        case 'no-speech':
          errorMsg = 'No speech detected. Please try again.';
          setState(STT_STATES.IDLE);
          break;
        case 'audio-capture':
          errorMsg = 'No microphone found. Please check your audio input.';
          setState(STT_STATES.ERROR);
          break;
        case 'network':
          errorMsg = 'Network error. Please check your connection.';
          setState(STT_STATES.ERROR);
          break;
        case 'aborted':
          log('Recognition aborted (normal for push-to-talk)');
          setState(STT_STATES.IDLE);
          return;
        default:
          errorMsg = `Speech recognition error: ${event.error}`;
          setState(STT_STATES.ERROR);
      }
      
      accumulatedRef.current = '';
      setErrorMessage(errorMsg);
      setInterimTranscript('');
      onError?.(errorMsg, event.error);
    };
    
    return recognition;
  }, [language, interimResults, onInterim, onError]);
  
  /**
   * Start listening (push-to-talk start)
   */
  const startListening = useCallback(() => {
    if (!isSpeechRecognitionSupported()) {
      setErrorMessage("Voice input isn't supported in this browser yet.");
      setState(STT_STATES.UNSUPPORTED);
      return false;
    }
    
    if (isListeningRef.current) {
      log('Already listening, ignoring start');
      return false;
    }
    
    try {
      // Clean up any existing recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore abort errors
        }
      }
      
      // Create new recognition instance
      recognitionRef.current = initRecognition();
      
      if (!recognitionRef.current) {
        return false;
      }
      
      // Clear previous transcript tracking
      accumulatedRef.current = '';
      setInterimTranscript('');
      setErrorMessage('');
      
      // Start recognition
      recognitionRef.current.start();
      isListeningRef.current = true;
      setState(STT_STATES.LISTENING);
      
      log('Started listening');
      return true;
    } catch (err) {
      log('Error starting recognition:', err);
      setErrorMessage('Failed to start voice input. Please try again.');
      setState(STT_STATES.ERROR);
      isListeningRef.current = false;
      onError?.('Failed to start voice input', err.message);
      return false;
    }
  }, [initRecognition, onError]);
  
  /**
   * Stop listening (push-to-talk release)
   */
  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListeningRef.current) {
      return;
    }
    
    try {
      log('Stopping recognition');
      recognitionRef.current.stop();
      setState(STT_STATES.PROCESSING);
    } catch (err) {
      log('Error stopping recognition:', err);
      // Force cleanup
      isListeningRef.current = false;
      setState(STT_STATES.IDLE);
    }
  }, []);
  
  /**
   * Abort listening (cancel without processing)
   */
  const abortListening = useCallback(() => {
    if (!recognitionRef.current) {
      return;
    }
    
    try {
      log('Aborting recognition');
      recognitionRef.current.abort();
    } catch (err) {
      log('Error aborting recognition:', err);
    }
    
    isListeningRef.current = false;
    setState(STT_STATES.IDLE);
    setInterimTranscript('');
  }, []);
  
  /**
   * Reset error state
   */
  const resetError = useCallback(() => {
    setErrorMessage('');
    setState(STT_STATES.IDLE);
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);
  
  return {
    state,
    isListening: state === STT_STATES.LISTENING,
    isProcessing: state === STT_STATES.PROCESSING,
    isSupported: state !== STT_STATES.UNSUPPORTED,
    isPermissionDenied: state === STT_STATES.PERMISSION_DENIED,
    hasError: state === STT_STATES.ERROR || state === STT_STATES.PERMISSION_DENIED,
    errorMessage,
    interimTranscript,
    startListening,
    stopListening,
    abortListening,
    resetError
  };
};

export default useSpeechToText;
